<template>
    <div class="custom-autocomplete relative flex align-items-center gap-2">
        <CustomLabel :name="name" :label="label" :required="required" :disabled="disabled"
                     :class="{'label-filled': internalValue != null && internalValue !== ''}" />
        <AutoComplete :id="name" @complete="innerCompleteFunction" :suggestions="items"
                      :field="field" v-model="internalValue" :dropdown="dropdown" :multiple="multiple"
                      :placeholder="computedPlaceholder" :disabled="disabled" :class="computedClasses"
                      :completeOnFocus="completeOnFocus" @blur="handleBlur" forceSelection />
        <CustomValidationMessage v-show="errorMessage" :errorMessage="errorMessage" />
        <CustomValidationMessage :errorMessage="customErrorMessage" />
        <CustomValidationMessage v-if="showRequiredError" :errorMessage="$t('message.validations.required')" />
    </div>
</template>

<script>
import AutoComplete from "primevue/autocomplete";
import CustomValidationMessage from "@/components/form/inner/CustomValidationMessage.vue";
import CustomLabel from "@/components/form/inner/CustomLabel.vue";
import {useField} from "vee-validate";

export default {
    name: "CustomAutocomplete",
    props: {
        name: {
            type: String,
        },
        label: {
            type: String,
        },
        emptyValue: {
            type: Boolean,
            default: true,
        },
        completeFunction: {
            type: Function,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        dropdown: {
            type: Boolean,
            default: true,
        },
        modelValue: null,
        rules: undefined,
        field: {
            type: String,
            default: "label",
        },
        itemFromQuery: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        customErrorMessage: {
            type: String,
            default: "",
        },
        showErrors: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        completeOnFocus: {
            type: Boolean,
            default: false,
        },
        width: {
            type: String,
            default: null,
        },
        noPlaceholder: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "",
        },
    },

    emits: ["update:modelValue", "change"],

    computed: {
        internalValue: {
            get() {
                if (this.selectedValue) {
                    return this.selectedValue;
                }
                if (this.modelValue) {
                    return this.modelValue;
                }
                return null;
            },
            set(value) {
                this.selectedValue = value;
                if (value && value.value) {
                    this.$emit("update:modelValue", value.value);
                    this.handleChange(value.value);
                } else {
                    this.$emit("update:modelValue", value);
                    this.handleChange(value);
                }
            },
        },
        showRequiredError() {
            return this.showErrors && (this.internalValue == null || this.internalValue === "");
        },
        computedClasses() {
            return (this.customErrorMessage !== "" || this.showRequiredError) ? "p-invalid" : "";
        },
        computedPlaceholder() {
            if (this.noPlaceholder) return "";
            if (this.placeholder === "") return this.$t('message.inputs.selectOnePlaceholder');
            else return this.placeholder;
        },
    },

    data() {
        return {
            items: [],
            selectedValue: null,
        };
    },

    components: {CustomLabel, CustomValidationMessage, AutoComplete},

    watch: {
        modelValue: {
            immediate: false,
            handler(newVal) {
                if (newVal === undefined || newVal === null) {
                    this.selectedValue = null;
                } else {
                    // Handle object mapping if needed
                    if (typeof newVal === 'object' && newVal !== null) {
                        this.selectedValue = newVal;
                    } else {
                        this.selectedValue = newVal;
                    }
                }
            },
        },
    },

    methods: {
        onChange(event) {
            if (event && event.value) {
                if (event.value.value === null) {
                    this.handleChange(null);
                    this.$emit("change", null);
                } else {
                    this.handleChange(event.value);
                    this.$emit("change", event.value.value);
                }
            }
        },

        innerCompleteFunction(event) {
            if (!this.completeFunction) return;

            const args = {
                query: event.query,
            };
            this.completeFunction(args).then((response) => {
                this.items = response.data;
                if (this.emptyValue) {
                    this.items = [{value: null, label: this.$t("message.other.emptyValue")}, ...this.items];
                }
            });
        },
    },

    setup(props) {
        const {
            value: inputValue,
            errorMessage,
            handleBlur,
            handleChange,
            meta,
        } = useField(props.name, props.rules, {
            initialValue: props.modelValue,
        });

        return {
            handleChange,
            handleBlur,
            errorMessage,
            inputValue,
            meta,
        };
    },
};
</script>

<style lang="scss" scoped>
.custom-autocomplete {
    .p-autocomplete {
        width: 100%;
    }
}
</style>
